import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useRouter } from 'shared-routing';
import { BasketDetails } from 'shared-types/Basket';
import { Option } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

import {
  cardAspectRatio,
  GalleryImage,
  ProductDetailTemplate,
  ProductFulfilmentItem,
  ProductOption,
  RecommendationItem,
  Recommendations,
} from 'voyager';

import { crossSellRoutesByType, Routes } from '@common/constants';
import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import { useGetSpaSettings } from '@features/spa';
import { useTranslations } from '@lang';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';
import { addItemToBasket } from '@utils/basket/addToBasket';
import { mergeCrossSellWithUpsell } from '@utils/helpers';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';

import { CrossSellEntry, ProductDetailEntity, ProductDetailV2Props } from './ProductDetailV2.types';

type AppState = { basket: { basketDetails: BasketDetails } } & Record<string, unknown>;

type RecommendationTarget = { route: string; id: string };

const mapCrossSellToRecommendations = (
  items: CrossSellEntry[]
): { items: RecommendationItem[]; targets: Map<string, RecommendationTarget> } => {
  const targets = new Map<string, RecommendationTarget>();
  const recommendations = items.reduce<RecommendationItem[]>((acc, { type, object }) => {
    const route = crossSellRoutesByType[type];
    if (!object?.name || !route) {
      return acc;
    }
    const key = `${type}-${object.id}`;
    targets.set(key, { route, id: String(object.id) });
    acc.push({
      id: key,
      name: object.name,
      imageUrl: object.imageGallery?.[0]?.url,
      price: object.price ?? null,
      discount: mapDiscountToDisplayObject(object.discount) ?? null,
    });
    return acc;
  }, []);

  return { items: recommendations, targets };
};

export const ProductDetailV2: FC<ProductDetailV2Props> = ({ id, language }) => {
  const { t } = useTranslations();
  const router = useRouter();
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, AnyAction>>();

  const property = useSelector(entitySelectors.getProperty) as Property;
  const product = useSelector((state) =>
    entitySelectors.getProduct(state, id)
  ) as ProductDetailEntity;
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as BasketDetails;

  const options = product.options ?? [];

  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(() => {
    const firstAvailable = options.find((option: Option) => option.quantity !== 0);
    return firstAvailable ? String(firstAvailable.id) : undefined;
  });
  const [quantity, setQuantity] = useState(1);
  const [isBasketSucceeded, setIsBasketSucceeded] = useState(false);

  const modules = Object.keys(property.modulesExcludingDisabled ?? {});
  const { data: spaSettings } = useGetSpaSettings(property.id);

  const selectedOption = options.find((option: Option) => String(option.id) === selectedOptionId);

  const images: GalleryImage[] = (product.imageGallery ?? []).map((image) => ({
    src: image.url,
    alt: product.name,
  }));

  const productOptions: ProductOption[] = options.map((option: Option) => ({
    id: String(option.id),
    label: option.name,
    disabled: option.quantity === 0,
  }));

  const priceSource = selectedOption?.price ?? product.price;
  const price =
    priceSource?.amount != null
      ? { amount: priceSource.amount, currencyCode: priceSource.currencyCode }
      : undefined;

  const fulfilment: ProductFulfilmentItem[] = [];
  if (property.deliveryCollection) {
    fulfilment.push({ type: 'collection', label: t('confirmation.collection') });
  }
  if (property.deliveryPost) {
    fulfilment.push({ type: 'delivery', label: t('confirmation.delivery') });
  }

  const crossSellItems = mergeCrossSellWithUpsell(
    product.upSell ?? null,
    'product',
    product.crossSellObjects ?? null
  ) as CrossSellEntry[];
  const { items: recommendationItems, targets: recommendationTargets } =
    mapCrossSellToRecommendations(crossSellItems);

  const recommendations =
    recommendationItems.length > 0 ? (
      <Recommendations
        items={recommendationItems}
        imageAspectRatio={cardAspectRatio.square.desktop}
        imageMobileAspectRatio={cardAspectRatio.square.mobile}
        onSelect={(recId) => {
          const target = recommendationTargets.get(recId);
          if (target) {
            router.push({ pathname: target.route, query: { id: target.id } });
          }
        }}
        title={t('product.otherProducts.title')}
        fromPriceLabel={t('common.from')}
        visibleCount={{ mobile: 1.2, desktop: 3 }}
      />
    ) : undefined;

  const handleAddToBasket = () => {
    addItemToBasket({
      type: 'products',
      data: {
        option: selectedOption ?? null,
        quantity,
        personalization: null,
        product: { id },
      },
      createBasket: (propertyId, lang, details) =>
        dispatch(createBasket(propertyId as number, lang, details)),
      updateBasket: (propertyId, basketId, lang, details) =>
        dispatch(updateBasket(propertyId as number, basketId, lang, details)),
      property,
      basketDetails,
      language,
      onSuccess: () => setIsBasketSucceeded(true),
    });
  };

  return (
    <>
      <BasketDrawerVersion
        open={isBasketSucceeded}
        onClose={() => setIsBasketSucceeded(false)}
        propertyModules={modules}
        hideArrivalTimes={spaSettings?.hideArrivalTimes ?? false}
      />
      <ProductDetailTemplate
        images={images}
        onBack={() => router.push({ pathname: Routes.PRODUCT_LIST })}
        viewAllLabel={t('common.viewAll')}
        previousImageLabel={t('product.previousImage')}
        nextImageLabel={t('product.nextImage')}
        goToImageLabel={(position, total) => t('product.viewImage', { position, total })}
        title={product.name}
        price={price}
        discount={mapDiscountToDisplayObject(product.discount)}
        promotionName={product.discount?.name}
        options={productOptions}
        selectedOptionId={selectedOptionId}
        onSelectOption={setSelectedOptionId}
        optionsLabel={t('product.select.option')}
        quantity={quantity}
        onQuantityChange={setQuantity}
        minQuantity={1}
        maxQuantity={
          product.purchaseLimit && product.purchaseLimit > 0 ? product.purchaseLimit : undefined
        }
        quantityLabel={t('product.select.quantity')}
        onAddToBasket={handleAddToBasket}
        addToBasketDisabled={product.purchaseLimit === 0 || selectedOption?.quantity === 0}
        addToBasketLabel={
          product.purchaseLimit === 0 ? t('product.comingSoon') : t('product.ctaButton')
        }
        fulfilment={fulfilment}
        description={product.summaryDescription}
        showMoreLabel={t('common.readMore')}
        showLessLabel={t('common.readLess')}
        specifications={product.description}
        specificationsLabel={t('product.specifications')}
        recommendations={recommendations}
      />
    </>
  );
};
